import type { RowAction } from '@/components/ui/data-table/types';
import { accountMutations } from '@/features/accounts/accounts.queries';
import AuthService from '@/services/AuthService';
import { executeMutation } from '@/utils/executeMutation';
import type { AdminAccount, AdminAccountPatch } from '@certhon/models';
import {
  Mail,
  RefreshCw,
  KeyRound,
  CalendarPlus,
  KeySquare,
  ShieldCheck,
  ShieldOff,
} from 'lucide-react';

export { adaptActionsForPanel } from '@/utils/adaptActionsForPanel';

/** The acting admin receives a copy of every account mail they trigger. */
function getAdminBcc(): string | undefined {
  return AuthService.getInstance().getCurrentUser()?.email || undefined;
}

/**
 * Apply a patch to a list of accounts sequentially.
 *
 * Bulk handlers reuse the per-account `update` mutation in a loop (the API has
 * no batched account endpoint). Iterating inside `onCalledBulk` rather than
 * using `tier: 'sequential'` lets us skip the per-row `confirm()` prompts that
 * `onCalled` issues for the row-level UX — the bulk runner shows its own
 * confirmation dialog.
 */
async function applyPatchToAccounts(
  accounts: AdminAccount[],
  buildPatch: (account: AdminAccount) => Omit<AdminAccountPatch, 'recnum'>,
): Promise<void> {
  const mutation = accountMutations.update();
  for (const account of accounts) {
    await executeMutation(mutation, {
      recnum: account.recnum,
      ...buildPatch(account),
    } as AdminAccountPatch);
  }
}

/**
 * Get actions for an account - contextual based on account state.
 */
export function getAccountActions(
  account: AdminAccount,
): RowAction<AdminAccount>[] {
  const actions: RowAction<AdminAccount>[] = [];

  // === ACCOUNT ACTIONS ===

  // Send invitation (only if no loginname - new account)
  if (!account.loginname) {
    actions.push({
      id: 'send-invitation',
      icon: Mail,
      label: 'Send Invitation',
      group: 'Account',
      confirmInBulk: true,
      onCalled: async () => {
        if (!confirm('Send an activation invitation email to this account?'))
          return;
        await executeMutation(accountMutations.update(), {
          recnum: account.recnum,
          send_invitation: true,
          bcc: getAdminBcc(),
        });
      },
      bulk: {
        tier: 'bulk',
        onCalledBulk: async (accounts) => {
          await applyPatchToAccounts(accounts, () => ({
            send_invitation: true,
            bcc: getAdminBcc(),
          }));
        },
      },
    });
  }

  // Force reactivation (only if has loginname)
  if (account.loginname) {
    actions.push({
      id: 'force-reactivation',
      icon: RefreshCw,
      label: 'Force Reactivation',
      group: 'Account',
      confirmInBulk: true,
      onCalled: async () => {
        const reason = prompt('Enter reason for reactivation (optional):');
        if (reason === null) return; // User cancelled

        await executeMutation(accountMutations.update(), {
          recnum: account.recnum,
          force_reactivation: true,
          reactivation_reason: reason || undefined,
          bcc: getAdminBcc(),
        });
      },
      bulk: {
        tier: 'bulk',
        onCalledBulk: async (accounts) => {
          // Bulk reactivation skips the per-row reason prompt; backend
          // accepts an empty reason.
          await applyPatchToAccounts(accounts, () => ({
            force_reactivation: true,
            bcc: getAdminBcc(),
          }));
        },
      },
    });
  }

  // Extend expiration (only if has loginname). Backend accepts the literal
  // `expires_on: 'extend'` directive — no per-row date input required.
  if (account.loginname) {
    actions.push({
      id: 'extend-expiration',
      icon: CalendarPlus,
      label: 'Extend Expiration',
      group: 'Account',
      confirmInBulk: true,
      onCalled: async () => {
        if (
          !confirm(
            'Extend the account expiration by the default validity period?',
          )
        )
          return;
        await executeMutation(accountMutations.update(), {
          recnum: account.recnum,
          expires_on: 'extend',
        });
      },
      bulk: {
        tier: 'bulk',
        onCalledBulk: async (accounts) => {
          await applyPatchToAccounts(accounts, () => ({
            expires_on: 'extend',
          }));
        },
      },
    });
  }

  // === PASSWORD & AUTH ACTIONS ===

  // Force password reset (only if has loginname)
  if (account.loginname) {
    actions.push({
      id: 'force-password-reset',
      icon: KeyRound,
      label: 'Force Password Reset',
      group: 'Password & Auth',
      variant: 'destructive',
      onCalled: async () => {
        if (
          !confirm(
            'Force a password reset? The user will receive an email with reset instructions.',
          )
        )
          return;
        await executeMutation(accountMutations.update(), {
          recnum: account.recnum,
          force_password_reset: true,
          bcc: getAdminBcc(),
        });
      },
      bulk: {
        tier: 'bulk',
        onCalledBulk: async (accounts) => {
          await applyPatchToAccounts(accounts, () => ({
            force_password_reset: true,
            bcc: getAdminBcc(),
          }));
        },
      },
    });
  }

  // === TAG AUTHENTICATION ACTIONS ===

  // Pre-approve tag (only if no tag and not already pre-approved)
  if (
    !account.tag_id &&
    !account.tag_authentication_preapproved &&
    !account.requested_tag_id
  ) {
    actions.push({
      id: 'pre-approve-tag-auth',
      icon: ShieldCheck,
      label: 'Pre-approve Tag Auth (90 days)',
      group: 'Tag Authentication',
      confirmInBulk: true,
      onCalled: async () => {
        if (
          !confirm(
            'Pre-approve tag authentication for 90 days? The user can self-enroll with their door tag.',
          )
        )
          return;
        await executeMutation(accountMutations.update(), {
          recnum: account.recnum,
          tag_authentication_preapproved: true,
        });
      },
      bulk: {
        tier: 'bulk',
        onCalledBulk: async (accounts) => {
          await applyPatchToAccounts(accounts, () => ({
            tag_authentication_preapproved: true,
          }));
        },
      },
    });
  }

  // Remove pre-approval (only if pre-approved)
  if (account.tag_authentication_preapproved) {
    actions.push({
      id: 'remove-tag-pre-approval',
      icon: ShieldOff,
      label: 'Remove Tag Pre-approval',
      group: 'Tag Authentication',
      confirmInBulk: true,
      onCalled: async () => {
        if (!confirm('Remove tag authentication pre-approval?')) return;
        await executeMutation(accountMutations.update(), {
          recnum: account.recnum,
          tag_authentication_preapproved: false,
        });
      },
      bulk: {
        tier: 'bulk',
        onCalledBulk: async (accounts) => {
          await applyPatchToAccounts(accounts, () => ({
            tag_authentication_preapproved: false,
          }));
        },
      },
    });
  }

  // Disable tag auth (only if tag_id exists)
  if (account.tag_id) {
    actions.push({
      id: 'disable-tag-auth',
      icon: KeySquare,
      label: 'Disable Tag Authentication',
      group: 'Tag Authentication',
      variant: 'destructive',
      onCalled: async () => {
        if (
          !confirm(
            'Disable and clear tag authentication? This will remove the tag ID from the account.',
          )
        )
          return;
        await executeMutation(accountMutations.update(), {
          recnum: account.recnum,
          tag_id: null,
        });
      },
      bulk: {
        tier: 'bulk',
        onCalledBulk: async (accounts) => {
          await applyPatchToAccounts(accounts, () => ({
            tag_id: null,
          }));
        },
      },
    });
  }

  return actions;
}
